import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, formatError } from "@/lib/api";
import { useT } from "@/lib/i18n";

export default function BookingSuccess() {
  const { reference } = useParams();
  const t = useT();
  const [booking, setBooking] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/bookings/${reference}`)
      .then((r) => setBooking(r.data))
      .catch((e) => setErr(formatError(e)));
  }, [reference]);

  return (
    <div data-testid="booking-success" className="min-h-screen">
      <Header />
      <div className="mx-auto max-w-3xl px-6 py-24 lg:px-10">
        {err && <p className="text-[#EF4444]">{err}</p>}
        {booking && (
          <>
            <div className="flex items-center gap-4">
              <CheckCircle2
                className="h-14 w-14 text-[#10B981]"
                strokeWidth={1.5}
              />
              <div>
                <span className="tag-orange">{t("success.tag")}</span>
                <h1 className="mt-4 text-4xl font-black uppercase sm:text-5xl">
                  Anfrage eingegangen
                </h1>
              </div>
            </div>

            <p className="mt-8 text-white/70">
              Wir haben Ihre Anfrage erhalten und melden uns kurz mit einer
              finalen Terminbestätigung an{" "}
              <span className="mono text-white">{booking.customer_email}</span>.
            </p>

            <div className="mt-10 border border-[#F59E0B] bg-[#F59E0B]/5 p-6">
              <div className="mono text-[11px] uppercase tracking-[0.2em] text-white/60">
                Status
              </div>
              <div className="mt-2 text-lg font-black uppercase text-[#F59E0B]">
                Anfrage wird geprüft
              </div>
              <p className="mt-2 text-xs text-white/60">
                Sobald wir Ihren Wunschtermin bestätigt haben, bekommen Sie
                automatisch eine E-Mail.
              </p>
            </div>

            <div className="mt-6 border border-[#FF4B00] bg-[#FF4B00]/5 p-6">
              <div className="mono text-[11px] uppercase tracking-[0.2em] text-white/60">
                {t("success.reference")}
              </div>
              <div
                data-testid="booking-reference"
                className="mono mt-2 text-3xl font-bold text-[#FF4B00]"
              >
                {booking.reference}
              </div>
            </div>

            <div className="mt-10 divide-y divide-white/10 border border-white/10 bg-[#0d0d10]">
              <Row label={t("book.row.service")} value={booking.service_name} />
              <Row
                label={t("book.row.dateTime")}
                value={`${booking.date} · ${booking.time}`}
                mono
              />
              <Row
                label={t("book.row.vehicle")}
                value={`${booking.vehicle_make} ${booking.vehicle_model} (${booking.vehicle_year})`}
              />
              <Row label={t("book.row.plate")} value={booking.license_plate} mono />
              <Row
                label={t("book.row.price")}
                value={`${booking.price_eur.toFixed(2).replace(".", ",")} €`}
              />
            </div>

            <div className="mt-10">
              <Link
                to="/"
                data-testid="back-home"
                className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-xs font-bold uppercase tracking-widest hover:border-white"
              >
                <ArrowLeft className="h-4 w-4" />
                {t("success.backHome")}
              </Link>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between p-5">
      <span className="mono text-[11px] uppercase tracking-[0.2em] text-white/50">
        {label}
      </span>
      <span className={`text-sm text-white ${mono ? "mono" : ""}`}>
        {value}
      </span>
    </div>
  );
}
